import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActivationPlugin } from '@hub/plugin-sdk';
import { LoadPluginUseCase } from '@/application/use-cases/LoadPluginUseCase';
import { FederationPluginLoader } from '@/infrastructure/repositories/FederationPluginLoader';
import type { PluginRegistryEntry } from '@/domain/entities/PluginRegistryEntry';
import { MAX_LOAD_ATTEMPTS, waitBackoff } from '@/presentation/hooks/pluginLoadBackoff';

const loadPluginUseCase = new LoadPluginUseCase(new FederationPluginLoader());

export type PluginLoadState =
  | { kind: 'idle' }
  | { kind: 'loading'; attempt: number }
  | { kind: 'waiting'; attempt: number; secondsRemaining: number }
  | { kind: 'ready'; plugin: ActivationPlugin; mountKey: number; loadDurationMs: number; retryCount: number }
  | {
      kind: 'error-final';
      offline: boolean;
      loadDurationMs: number;
      retryCount: number;
      errorCode: string;
      errorMessage: string;
    };

export interface UsePluginLoadRetryResult {
  state: PluginLoadState;
  /** Inicia o carregamento (CA-04) — chamado explicitamente após o
   * participante decidir sobre o consent gate (CA-18), nunca por um
   * `useEffect` de montagem (mesmo raciocínio de
   * `usePluginRegistryResolution`, evita double-fetch sob `StrictMode`). */
  start: (entry: PluginRegistryEntry) => void;
  /** CA-10: reseta o contador e dispara nova sequência de 3 tentativas do
   * zero, a partir do último registro resolvido. */
  retryManually: () => void;
  /** Chamado pelo `PluginErrorBoundary` quando o plugin já montado lança um
   * erro em fase de render (única forma de capturar isso — `try/catch` não
   * alcança erros de render do React). Tratado com o mesmo contador/backoff
   * das falhas de carregamento (CA-06/07/08) — a próxima tentativa recarrega
   * o módulo do zero (novo `mountKey`), nunca apenas remonta o componente
   * que acabou de falhar. */
  reportRenderFailure: (error: unknown) => void;
}

/** CA-16 (mesma heurística usada em `LoadPluginUseCase`/`HttpEventConfigRepository`):
 * `TypeError` é o sinal de "requisição não pôde nem ser tentada". Erros de
 * render do `PluginErrorBoundary` nunca são classificados como offline —
 * são um bug de render do componente, não de rede. */
const RENDER_ERROR_CODE = 'PLUGIN_RENDER_ERROR';
const LOAD_ERROR_CODE = 'PLUGIN_LOAD_ERROR';

/**
 * Orquestra o carregamento de um plugin real via Module Federation (HUB-237,
 * CA-04 a CA-10): 3 tentativas automáticas com backoff exponencial (1s/2s/
 * 4s, CA-06/07/08/19), sucesso ou erro final com retentativa manual (CA-10).
 * Unifica o tratamento das duas fontes de falha possíveis — `Result` de
 * carregamento (`LoadPluginUseCase`) e erro de render capturado pelo
 * `PluginErrorBoundary` — no mesmo contador/backoff.
 */
export function usePluginLoadRetry(): UsePluginLoadRetryResult {
  const [state, setState] = useState<PluginLoadState>({ kind: 'idle' });
  const cancelledRef = useRef(false);
  const entryRef = useRef<PluginRegistryEntry | null>(null);
  const currentAttemptRef = useRef(1);
  const mountKeyRef = useRef(0);
  const startedAtRef = useRef(0);

  // `React.StrictMode` roda setup -> cleanup -> setup na montagem inicial
  // (dev/teste) — sem o reset explícito no setup, o cleanup simulado deixa
  // `cancelledRef.current` travado em `true` para sempre, fazendo a
  // primeira chamada real a `start()` abortar silenciosamente (achado desta
  // fatia, mesma classe de armadilha do double-POST da HUB-119).
  useEffect(() => {
    cancelledRef.current = false;
    return (): void => {
      cancelledRef.current = true;
    };
  }, []);

  const elapsedMs = useCallback((): number => Math.round(performance.now() - startedAtRef.current), []);

  const finalizeFailure = useCallback(
    (errorCode: string, errorMessage: string, offline: boolean, attempt: number): void => {
      setState({
        kind: 'error-final',
        offline,
        loadDurationMs: elapsedMs(),
        retryCount: attempt,
        errorCode,
        errorMessage,
      });
    },
    [elapsedMs]
  );

  const runAttempt = useCallback(
    async (entry: PluginRegistryEntry, attempt: number): Promise<void> => {
      currentAttemptRef.current = attempt;
      setState({ kind: 'loading', attempt });

      const result = await loadPluginUseCase.execute({
        id: entry.activationType,
        remoteUrl: entry.remoteUrl,
        moduleName: entry.moduleName,
        version: entry.version,
      });
      if (cancelledRef.current) return;

      if (result.success) {
        mountKeyRef.current += 1;
        setState({
          kind: 'ready',
          plugin: result.plugin,
          mountKey: mountKeyRef.current,
          loadDurationMs: elapsedMs(),
          retryCount: attempt - 1,
        });
        return;
      }

      if (attempt >= MAX_LOAD_ATTEMPTS) {
        finalizeFailure(LOAD_ERROR_CODE, result.error, result.offline, attempt);
        return;
      }

      await waitBackoff(
        attempt,
        (secondsRemaining) => setState({ kind: 'waiting', attempt, secondsRemaining }),
        () => cancelledRef.current
      );
      if (cancelledRef.current) return;
      await runAttempt(entry, attempt + 1);
    },
    [elapsedMs, finalizeFailure]
  );

  const start = useCallback(
    (entry: PluginRegistryEntry): void => {
      entryRef.current = entry;
      startedAtRef.current = performance.now();
      void runAttempt(entry, 1);
    },
    [runAttempt]
  );

  const retryManually = useCallback((): void => {
    const entry = entryRef.current;
    if (!entry) return;
    startedAtRef.current = performance.now();
    void runAttempt(entry, 1);
  }, [runAttempt]);

  const reportRenderFailure = useCallback(
    (error: unknown): void => {
      const entry = entryRef.current;
      if (!entry) return;
      const attempt = currentAttemptRef.current;
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao renderizar o plugin.';

      if (attempt >= MAX_LOAD_ATTEMPTS) {
        finalizeFailure(RENDER_ERROR_CODE, message, false, attempt);
        return;
      }

      void (async (): Promise<void> => {
        await waitBackoff(
          attempt,
          (secondsRemaining) => setState({ kind: 'waiting', attempt, secondsRemaining }),
          () => cancelledRef.current
        );
        if (cancelledRef.current) return;
        await runAttempt(entry, attempt + 1);
      })();
    },
    [finalizeFailure, runAttempt]
  );

  return { state, start, retryManually, reportRenderFailure };
}
